import { useCallback, useContext, useEffect, useRef } from "react";
import {
  type ApolloError,
  type FetchResult,
  type MutationFunctionOptions,
  type OperationVariables,
  useMutation,
} from "@apollo/client";
import * as Sentry from "@sentry/nextjs";
import type { DocumentNode } from "graphql";
import { print } from "graphql";

import { LoadingContext } from "@/hooks/global-state/loadingContext";
import { SnackbarContext } from "@/hooks/global-state/snackbar";
import { analysisEventKey, trackAnalysisEvent } from "@/utils/analysis/event";
import { handleErrorMessage } from "@/utils/error/handleErrorMessage";
import { loadingDecorator } from "@/utils/loading/loadingDecorator";

type AnyJson = Record<string, unknown>;

/**
 * Loading付きのGraphQL Mutationを扱う
 */
export function useGraphqlMutation<
  TData = AnyJson,
  TVariables extends OperationVariables = AnyJson,
>(mutation: DocumentNode, variables?: TVariables, shouldSkipThrowError = false) {
  const { setIsLoading } = useContext(LoadingContext);
  const { setSnackbar } = useContext(SnackbarContext);
  const mutationStartTimeRef = useRef<number | undefined>(undefined);

  const [mutate, { loading, error, data }] = useMutation<TData, TVariables>(mutation, {
    variables,
  });

  const mutateWithLoading = useCallback(
    async (
      options?: MutationFunctionOptions<TData, TVariables>,
    ): Promise<FetchResult<TData> | null> => {
      return await loadingDecorator(async () => {
        const startTime = performance.now();
        mutationStartTimeRef.current = startTime;

        try {
          const mutateResult = await mutate(options);

          // レイテンシの計算
          const endTime = performance.now();
          const duration = endTime - startTime;

          // analysis event の記録
          const { mutation, variables } = options ?? {};
          trackAnalysisEvent(analysisEventKey.GRAPHQL_MUTATION_SUCCEEDED, {
            mutation: mutation !== undefined ? print(mutation) : undefined,
            variables,
            duration,
          });
          mutationStartTimeRef.current = undefined;

          return mutateResult;
        } catch (error) {
          // snackbar 表示対象エラーはここで握りつぶし、未処理例外として外に漏らさない
          if (handleErrorMessage(error as ApolloError)) {
            return null;
          }
          throw error;
        }
      }, setIsLoading);
    },
    [mutate, setIsLoading],
  );

  useEffect(() => {
    setIsLoading(loading);
  }, [loading, setIsLoading]);

  useEffect(() => {
    if (!error) {
      return;
    }

    // analysis event の記録
    trackAnalysisEvent(analysisEventKey.GRAPHQL_MUTATION_FAILED, {
      mutation: print(mutation),
      variables,
      error: error.message,
      startTime: mutationStartTimeRef.current,
    });
    mutationStartTimeRef.current = undefined;

    const message = handleErrorMessage(error);
    if (message) {
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } else if (shouldSkipThrowError) {
      // Backend側で通知しているので通知は不要
      if (!error.message.includes("internal server error")) {
        // 他のエラーはError Boundaryで処理している
        Sentry.captureException(error, {
          extra: {
            componentStack: error.stack,
            mutation: print(mutation),
            variables,
          },
        });
      }
    } else {
      throw error;
    }
  }, [error, mutation, setSnackbar, shouldSkipThrowError, variables]);

  return { data, mutate: mutateWithLoading };
}

// どこかの Mutationでエラーが起きたら、それをthrowしてcatchで処理する
export const exitOnMutationError = <T>(res: FetchResult<T> | null): T | null => {
  if (res === null) return null;
  if (res.errors) {
    // ベストプラクティスがわからない。全部投げたいがとりあえず最初のエラーを投げる
    throw res.errors[0];
  }
  if (res.data == null) {
    throw new Error("No data returned from mutation");
  }
  return res.data;
};
